/**
 * The shape of a commissioning conversation, shared by the two sentinels Tanjong
 * Life is asked to commission on stage.
 *
 * Both beats are the same nine moves and differ only in what the agent watches,
 * so the moves live here once and each scenario file carries its own words. The
 * order is load-bearing: the authorities are offered with NONE pre-selected and
 * refused out loud BEFORE the connected systems are offered with all of them
 * ticked, because "here is everything it could be allowed to do, and it takes
 * none of it" only lands if the room sees the list first.
 *
 * `deployAgent` hands over the WHOLE detail bundle. The mutator strips the
 * stream (`demo-born.ts`) so the agent's page can play it back as a live boot,
 * and narrows `detail.skills.apps` to `patches.apps` — so unticking a system in
 * the second question really is honoured, on the commission write and again on
 * the boot. Nothing here pre-strips or pre-filters.
 */
import type { Agent, ThinkStep } from '@wand/shared';
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';
import type { GAgent } from '@wand/shared/dataset/groups.js';
import type { Intent } from '@wand/shared/text/intent.js';
import type { Scenario, ScenarioStep } from '../director.js';
import type { Choice } from '../../gateway/wizard.js';

export interface CommissionAsset {
  agent: Agent;
  detail: AgentDetailContent;
}

export interface CommissionConfig {
  /** Scenario id; must match the file name and `registry.SCENARIO_IDS`. */
  id: string;
  keywords: string[];
  /** The paraphrases. Consulted only on a total keyword miss, from the wizard, and alone. */
  intents?: Intent[];
  /** The agent's pk — the boot-asset key, the group member and the link target. */
  pk: string;
  name: string;
  /** The bundle, loaded once and memoised by the caller. */
  load: () => Promise<CommissionAsset>;
  /** The read of the org that precedes the offer. */
  thinking: ThinkStep[];
  /** What it will watch, and what the desk already admits. */
  watch: { text: string; sources?: string[] };
  /** The sentence that introduces the authority list. */
  offer: string;
  authorities: Choice[];
  /** Why it takes none of them. */
  refusal: string;
  /** The connected systems the org graph offers; every one is pre-ticked. */
  apps: Choice[];
  appsPrompt: string;
  /** Group membership and knowledge-base readership landed with the commission. */
  group: { pk: string; member: GAgent };
  kb: { id: string; scope: string };
  /** The mutation summary and the two closing lines. */
  summary: string;
  commissioned: string;
  linkLabel: string;
}

/** The nine moves, in the one order that reads honestly. */
export function makeCommission(cfg: CommissionConfig): Scenario {
  const appIds = cfg.apps.map((a) => a.id);
  const steps: ScenarioStep[] = [
    { do: 'thinking', steps: cfg.thinking, delay: 700 },
    { do: 'say', text: cfg.watch.text, sources: cfg.watch.sources, delay: 1200 },
    { do: 'say', text: cfg.offer, delay: 1000 },
    {
      do: 'askMulti',
      id: 'authorities',
      prompt: `Which authorities should the ${cfg.name} hold? None is pre-selected.`,
      options: cfg.authorities,
      preselect: [],
      delay: 700,
    },
    { do: 'say', text: cfg.refusal, delay: 900 },
    {
      do: 'askMulti',
      id: 'apps',
      prompt: cfg.appsPrompt,
      options: cfg.apps,
      preselect: appIds,
      delay: 800,
    },
    {
      do: 'deployAgent',
      agent: () => cfg.load().then((a) => a.agent),
      detail: () => cfg.load().then((a) => a.detail),
      // `storyTokens` here is what makes the commission write resolve the
      // bundle's stamps: `deployAgent` looks for the marker on the agent, the
      // detail or these patches, and the asset carries it only at its top level.
      // The answer to the question above, not the list it was offered with: a system the
      // person unticked must not be written. `askMulti` returns the chosen ids joined by
      // `+`; nothing chosen at all is a real answer and means no connected system.
      patches: (answer?: string) => ({
        storyTokens: true as const,
        apps: answer === undefined ? appIds : answer.split('+').filter(Boolean),
        groups: [{ groupPk: cfg.group.pk, agent: cfg.group.member }],
        kb: [{ kbId: cfg.kb.id, reader: { agentPk: cfg.pk, scope: cfg.kb.scope } }],
      }),
      summary: cfg.summary,
      delay: 1000,
    },
    { do: 'say', text: cfg.commissioned, delay: 700 },
    {
      do: 'link',
      label: cfg.linkLabel,
      href: `/agents/${cfg.pk}?chat=new`,
      delay: 600,
    },
  ];
  /*
   * `surface: 'chat'` is set HERE and not in the two scenario files, so a third commission
   * cannot forget it. It is what stops "build an autonomous agent called SIU Triage
   * Sentinel …" being taken by the generic agent builder, which would answer with a
   * contract-review agent and persist it into this org under a name it invented. These
   * nine moves contain no canvas step: the dock is their only correct home.
   */
  return {
    id: cfg.id,
    orgId: 'tanjong-life',
    keywords: cfg.keywords,
    intents: cfg.intents,
    surface: 'chat',
    steps,
  };
}

/** Memoise a bundle import so a replayed scenario loads its chunk once. */
export function once(load: () => Promise<CommissionAsset>): () => Promise<CommissionAsset> {
  let p: Promise<CommissionAsset> | null = null;
  return () => {
    if (!p) p = load();
    return p;
  };
}

/** The five authorities every sentinel in this act is offered, and refuses. */
export const CLAIMS_AUTHORITIES: Choice[] = [
  { id: 'rescore', label: 'Re-score a claim' },
  { id: 'hold-release', label: 'Hold or release a settlement' },
  { id: 'refer-clear', label: 'Open or clear a referral' },
  { id: 'thresholds', label: 'Change a threshold or an indicator set' },
  { id: 'messages', label: 'Message a claimant or an adviser' },
];
